import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { ArrowLeft, ShieldCheck, ShieldX, LogIn, Eye, RefreshCw } from "lucide-react";
import { requireAdmin } from "@/lib/admin-guard";
import { supabase } from "@/integrations/supabase/client";

type AuditRow = {
  id: string;
  user_id: string;
  user_email: string | null;
  event_type: "login" | "role_check" | "route_visit";
  route: string | null;
  role_granted: boolean | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
};

type EventFilter = "all" | "login" | "role_check" | "route_visit";

export const Route = createFileRoute("/admin_/access-audit")({
  ssr: false,
  beforeLoad: requireAdmin,
  head: () => ({
    meta: [
      { title: "Access Audit — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AccessAuditPage,
});

function AccessAuditPage() {
  const [eventFilter, setEventFilter] = useState<EventFilter>("all");
  const [emailFilter, setEmailFilter] = useState("");
  const [limit, setLimit] = useState(200);

  const { data, isLoading, isFetching, refetch, error } = useQuery({
    queryKey: ["admin", "access-audit", eventFilter, emailFilter, limit],
    queryFn: async (): Promise<AuditRow[]> => {
      let q = supabase
        .from("admin_access_audit")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(limit);
      if (eventFilter !== "all") q = q.eq("event_type", eventFilter);
      if (emailFilter.trim()) q = q.ilike("user_email", `%${emailFilter.trim()}%`);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as AuditRow[];
    },
  });

  const rows = data ?? [];

  const summary = {
    total: rows.length,
    logins: rows.filter((r) => r.event_type === "login").length,
    visits: rows.filter((r) => r.event_type === "route_visit").length,
    denied: rows.filter((r) => r.event_type === "role_check" && r.role_granted === false).length,
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <div className="mb-6 flex items-center justify-between gap-3">
        <div>
          <Link
            to="/admin"
            search={{ tab: "overview" } as never}
            className="mb-2 inline-flex items-center gap-1 text-sm text-foreground/70 hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Admin
          </Link>
          <h1 className="font-display text-2xl text-foreground sm:text-3xl">
            Admin Access Audit
          </h1>
          <p className="mt-1 text-sm text-foreground/70">
            Every admin login, role check, and visited admin route, with timestamps.
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-background px-4 py-2 text-sm hover:bg-muted"
          disabled={isFetching}
        >
          <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <SummaryCard label="Events shown" value={summary.total} />
        <SummaryCard label="Logins" value={summary.logins} icon={<LogIn className="h-4 w-4" />} />
        <SummaryCard label="Route visits" value={summary.visits} icon={<Eye className="h-4 w-4" />} />
        <SummaryCard label="Denied checks" value={summary.denied} icon={<ShieldX className="h-4 w-4 text-red-600" />} />
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1 rounded-full border border-gold/30 bg-card p-1 text-xs">
          {(["all", "login", "role_check", "route_visit"] as EventFilter[]).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setEventFilter(f)}
              className={`rounded-full px-3 py-1.5 capitalize transition ${
                eventFilter === f ? "bg-foreground text-background" : "text-foreground/70 hover:text-foreground"
              }`}
            >
              {f.replace("_", " ")}
            </button>
          ))}
        </div>
        <input
          type="search"
          placeholder="Filter by email…"
          value={emailFilter}
          onChange={(e) => setEmailFilter(e.target.value)}
          className="min-w-[220px] flex-1 rounded-md border border-gold/30 bg-background px-3 py-2 text-sm"
        />
        <select
          value={limit}
          onChange={(e) => setLimit(Number(e.target.value))}
          className="rounded-md border border-gold/30 bg-background px-3 py-2 text-sm"
        >
          <option value={100}>Last 100</option>
          <option value={200}>Last 200</option>
          <option value={500}>Last 500</option>
          <option value={1000}>Last 1000</option>
        </select>
      </div>

      {error ? (
        <div className="rounded-xl border border-red-300 bg-red-50 p-4 text-sm text-red-800">
          {(error as Error).message}
        </div>
      ) : isLoading ? (
        <div className="rounded-xl border border-gold/20 bg-card p-6 text-sm text-foreground/60">
          Loading audit events…
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-gold/20 bg-card p-6 text-center text-sm text-foreground/60">
          No audit events match your filters yet.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gold/20 bg-card">
          <table className="min-w-full text-sm">
            <thead className="bg-foreground/5 text-start text-xs uppercase tracking-wider text-foreground/60">
              <tr>
                <th className="px-3 py-2">When</th>
                <th className="px-3 py-2">User</th>
                <th className="px-3 py-2">Event</th>
                <th className="px-3 py-2">Route</th>
                <th className="px-3 py-2">Result</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-gold/10">
                  <td className="px-3 py-2 align-top whitespace-nowrap text-foreground/80">
                    {formatTimestamp(r.created_at)}
                  </td>
                  <td className="px-3 py-2 align-top">
                    <div className="text-foreground">{r.user_email ?? "—"}</div>
                    <div className="font-mono text-[10px] text-foreground/50">{r.user_id.slice(0, 8)}…</div>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <EventBadge type={r.event_type} />
                  </td>
                  <td className="px-3 py-2 align-top font-mono text-xs text-foreground/80">
                    {r.route ?? "—"}
                  </td>
                  <td className="px-3 py-2 align-top">
                    {r.role_granted === true ? (
                      <span className="inline-flex items-center gap-1 text-green-700">
                        <ShieldCheck className="h-3.5 w-3.5" /> granted
                      </span>
                    ) : r.role_granted === false ? (
                      <span className="inline-flex items-center gap-1 text-red-700">
                        <ShieldX className="h-3.5 w-3.5" /> denied
                      </span>
                    ) : (
                      <span className="text-foreground/40">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  icon,
}: {
  label: string;
  value: number;
  icon?: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-gold/20 bg-card p-3">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-foreground/60">
        {icon} {label}
      </div>
      <div className="mt-1 font-display text-2xl text-foreground">{value}</div>
    </div>
  );
}

function EventBadge({ type }: { type: AuditRow["event_type"] }) {
  const styles: Record<AuditRow["event_type"], string> = {
    login: "bg-blue-100 text-blue-800",
    role_check: "bg-amber-100 text-amber-800",
    route_visit: "bg-emerald-100 text-emerald-800",
  };
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${styles[type]}`}>
      {type.replace("_", " ")}
    </span>
  );
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}
